import { type BasicObservable, Specs } from '@doxyme/extensions-common';
import type Instance from '../../../instance';
import AppletCapability from '../applet';

const { HeadlessApplet } = Specs;

const { Actions, Events } = HeadlessApplet;

type Constraints = Specs.HeadlessApplet.Constraints;
type CustomActionPayload = Specs.HeadlessApplet.CustomActionPayload;
type Applet = Specs.HeadlessApplet.Applet;

/**
 * Headless Applet is intended to act as a runtime/brain when there isn't any UI needed.
 */
export default class HeadlessAppletCapability extends AppletCapability<Constraints> {
  static id = HeadlessApplet.id;

  constructor() {
    super(HeadlessApplet);
    this.handlers.push(
      [Actions.Attach, this.attach.bind(this)],
      [Actions.Detach, this.detach.bind(this)],
      [Actions.CustomAction, this.handleCustomAction.bind(this)],
    );
  }

  /**
   * Get all frames registered to this capability
   * @description This is an observable that will update whenever frames are added or removed.
   * See {@link BasicObservable} for more information.
   * @returns All frames registered to this capability, as an observable.
   * @example
   * ```ts
   * const frames = headlessApplet.getFrames();
   * frames.onChange((frames) => {
   *  console.log('Frames changed', frames);
   * });
   * ```
   */
  getFrames(): BasicObservable<Applet[]> {
    return this._frames;
  }

  /**
   * Headless Applets leverages a paired custom action / custom event system to pass messages between applets.
   * This includes the headless applet. It will take an action from one applet and then broadcast it as a custom event to any applet(s) listening.
   */
  private async handleCustomAction(
    payload: CustomActionPayload,
    instance: Instance,
  ): Promise<void> {
    instance.notify([Events.CustomEvent, payload]);
  }
}
